import React, { useState, useEffect } from 'react';
import {
  Card, Steps, Select, Typography, Button, Table, Tag, message,
  InputNumber, Space, Alert, Descriptions, Spin, Input, Row, Col, Modal
} from 'antd';
import { MailOutlined, SendOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import { emailApi } from 'api/email';
import { usersApi, UserListParams } from 'api/users';
import styled from 'styled-components';

const { Title, Text } = Typography;
const { TextArea } = Input;

const StepCard = styled(Card)`
  margin-bottom: 16px;
  border-radius: 8px;
`;

type SegmentType = 'all' | 'free' | 'paid' | 'trial' | 'active_7d' | 'inactive_30d' | 'custom_ids';

const SEGMENT_OPTIONS = [
  { label: 'All Users', value: 'all' },
  { label: 'Free Users', value: 'free' },
  { label: 'Paid Users (Pro + Enterprise)', value: 'paid' },
  { label: 'Free Trial Users', value: 'trial' },
  { label: 'Active Last 7 Days', value: 'active_7d' },
  { label: 'Inactive 30+ Days', value: 'inactive_30d' },
  { label: 'Selected Users', value: 'custom_ids' },
];

const Email: React.FC = () => {
  const location = useLocation();
  const preselectedIds = (location.state as any)?.selectedUserIds || [];

  const [currentStep, setCurrentStep] = useState(0);
  const [segment, setSegment] = useState<SegmentType>(preselectedIds.length > 0 ? 'custom_ids' : 'all');
  const [templates, setTemplates] = useState<any[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<string | undefined>();
  const [matchedUsers, setMatchedUsers] = useState<any[]>([]);
  const [matchedCount, setMatchedCount] = useState(0);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [sending, setSending] = useState(false);
  const [mergeVars, setMergeVars] = useState<Record<string, string>>({});
  const [sendResult, setSendResult] = useState<any>(null);

  useEffect(() => { fetchTemplates(); }, []);

  useEffect(() => {
    if (currentStep >= 1) {
      fetchMatchedUsers();
    }
  }, [segment, currentStep]);

  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const res = await emailApi.getTemplates();
      setTemplates(res.data.templates || []);
    } catch (err) {
      message.error('Failed to load email templates');
    } finally {
      setLoadingTemplates(false);
    }
  };

  const fetchMatchedUsers = async () => {
    setLoadingUsers(true);
    try {
      const params: UserListParams = { limit: 10, page: 1 };

      switch (segment) {
        case 'free': params.status = 'free'; break;
        case 'paid': params.status = 'professional'; break;
        case 'trial': params.status = 'free_trial'; break;
        case 'active_7d':
          params.active_since = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
          break;
        case 'inactive_30d':
          params.inactive_since = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
          break;
      }

      if (segment === 'custom_ids') {
        setMatchedCount(preselectedIds.length);
        // Fetch details for preview
        const previews = [];
        for (const id of preselectedIds.slice(0, 10)) {
          try {
            const res = await usersApi.getDetail(id);
            previews.push(res.data.user);
          } catch {}
        }
        setMatchedUsers(previews);
      } else {
        // Fetch count using a large limit=1 query
        const countRes = await usersApi.list({ ...params, limit: 1 });
        setMatchedCount(countRes.data.total || 0);
        // Fetch preview users
        const previewRes = await usersApi.list({ ...params, limit: 10 });
        setMatchedUsers(previewRes.data.data || []);
      }
    } catch (err) {
      message.error('Failed to fetch matching users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSend = async () => {
    if (!selectedTemplate) {
      message.error('Please select a template');
      return;
    }

    setSending(true);
    try {
      let res;
      if (segment === 'custom_ids') {
        res = await emailApi.sendBulk(selectedTemplate, preselectedIds, mergeVars);
      } else {
        const segmentFilter: Record<string, any> = {};
        switch (segment) {
          case 'free': segmentFilter.status = 'free'; break;
          case 'paid': segmentFilter.status = 'professional'; break;
          case 'trial': segmentFilter.status = 'free_trial'; break;
          case 'active_7d':
            segmentFilter.active_since = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
            break;
          case 'inactive_30d':
            segmentFilter.inactive_since = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
            break;
          // 'all' = empty filter = all users
        }
        res = await emailApi.sendToSegment(selectedTemplate, segmentFilter, mergeVars);
      }
      setSendResult(res.data);
      message.success(res.data.messages?.[0] || 'Emails sent!');
      setCurrentStep(4);
    } catch (err: any) {
      message.error(err.response?.data?.messages?.[0] || 'Failed to send emails');
    } finally {
      setSending(false);
    }
  };

  const previewColumns = [
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Name', dataIndex: 'name', key: 'name', render: (n: string) => n || '-' },
    { title: 'Status', dataIndex: 'status', key: 'status',
      render: (s: string) => <Tag>{s}</Tag> },
  ];

  const steps = [
    { title: 'Select Segment' },
    { title: 'Preview Users' },
    { title: 'Pick Template' },
    { title: 'Confirm & Send' },
    { title: 'Done' },
  ];

  return (
    <div>
      <Title level={2}>Bulk Email</Title>

      <Steps current={currentStep} items={steps} style={{ marginBottom: 24 }} />

      {/* Step 0: Select Segment */}
      {currentStep === 0 && (
        <StepCard title="Step 1: Select User Segment">
          <Select
            value={segment}
            onChange={(val) => setSegment(val as SegmentType)}
            style={{ width: 400 }}
            options={SEGMENT_OPTIONS.filter(o =>
              o.value !== 'custom_ids' || preselectedIds.length > 0
            )}
            size="large"
          />
          {segment === 'custom_ids' && (
            <Alert
              message={`${preselectedIds.length} users selected from Users page`}
              type="info"
              style={{ marginTop: 12 }}
            />
          )}
          <div style={{ marginTop: 16 }}>
            <Button type="primary" onClick={() => setCurrentStep(1)}>
              Next: Preview Users
            </Button>
          </div>
        </StepCard>
      )}

      {/* Step 1: Preview matched users */}
      {currentStep === 1 && (
        <StepCard title="Step 2: Preview Matched Users">
          {loadingUsers ? (
            <Spin />
          ) : (
            <>
              <Alert
                message={`${matchedCount} users match this segment`}
                type="info"
                style={{ marginBottom: 12 }}
              />
              <Text type="secondary" style={{ marginBottom: 12, display: 'block' }}>
                Showing first {Math.min(10, matchedUsers.length)} users:
              </Text>
              <Table
                dataSource={matchedUsers}
                columns={previewColumns}
                rowKey="_id"
                size="small"
                pagination={false}
              />
              <Space style={{ marginTop: 16 }}>
                <Button onClick={() => setCurrentStep(0)}>Back</Button>
                <Button type="primary" onClick={() => setCurrentStep(2)}
                  disabled={matchedCount === 0}>
                  Next: Pick Template
                </Button>
              </Space>
            </>
          )}
        </StepCard>
      )}

      {/* Step 2: Pick Template */}
      {currentStep === 2 && (
        <StepCard title="Step 3: Select Email Template">
          {loadingTemplates ? (
            <Spin />
          ) : (
            <>
              <Select
                value={selectedTemplate}
                onChange={setSelectedTemplate}
                style={{ width: '100%', maxWidth: 500 }}
                placeholder="Select a Mandrill template..."
                size="large"
                showSearch
                optionFilterProp="label"
                options={templates.map(t => ({
                  label: `${t.name} (${t.slug})`,
                  value: t.slug,
                }))}
              />
              {selectedTemplate && (
                <Card size="small" style={{ marginTop: 12 }}>
                  {(() => {
                    const t = templates.find(t => t.slug === selectedTemplate);
                    return t ? (
                      <Descriptions size="small" column={1}>
                        <Descriptions.Item label="Name">{t.name}</Descriptions.Item>
                        <Descriptions.Item label="Subject">{t.subject || '-'}</Descriptions.Item>
                        <Descriptions.Item label="From">{t.from_name} &lt;{t.from_email}&gt;</Descriptions.Item>
                      </Descriptions>
                    ) : null;
                  })()}
                </Card>
              )}
              <div style={{ marginTop: 16 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                  Extra merge variables (optional):
                </Text>
                <Row gutter={[8, 8]}>
                  <Col span={8}>
                    <Input
                      placeholder="Variable name (e.g. PROMO_CODE)"
                      onChange={(e) => {
                        const key = e.target.value;
                        if (key) setMergeVars(prev => ({ ...prev, [key]: prev[key] || '' }));
                      }}
                    />
                  </Col>
                  <Col span={8}>
                    <Input
                      placeholder="Value"
                      onChange={(e) => {
                        const keys = Object.keys(mergeVars);
                        if (keys.length > 0) {
                          setMergeVars(prev => ({ ...prev, [keys[keys.length - 1]]: e.target.value }));
                        }
                      }}
                    />
                  </Col>
                </Row>
              </div>
              <Space style={{ marginTop: 16 }}>
                <Button onClick={() => setCurrentStep(1)}>Back</Button>
                <Button type="primary" onClick={() => setCurrentStep(3)}
                  disabled={!selectedTemplate}>
                  Next: Review & Send
                </Button>
              </Space>
            </>
          )}
        </StepCard>
      )}

      {/* Step 3: Confirm */}
      {currentStep === 3 && (
        <StepCard title="Step 4: Confirm & Send">
          <Alert
            message={`You are about to send emails to ${matchedCount} users`}
            description={
              <div>
                <p><strong>Template:</strong> {selectedTemplate}</p>
                <p><strong>Segment:</strong> {SEGMENT_OPTIONS.find(s => s.value === segment)?.label}</p>
                {Object.keys(mergeVars).length > 0 && (
                  <p><strong>Merge vars:</strong> {JSON.stringify(mergeVars)}</p>
                )}
              </div>
            }
            type="warning"
            style={{ marginBottom: 16 }}
          />
          <Space>
            <Button onClick={() => setCurrentStep(2)}>Back</Button>
            <Modal
              title={`Send to ${matchedCount} users?`}
              open={false}
            >
              <p>This action cannot be undone.</p>
            </Modal>
            <Button
              type="primary"
              danger
              icon={<SendOutlined />}
              loading={sending}
              onClick={() => {
                Modal.confirm({
                  title: `Send to ${matchedCount} users?`,
                  content: 'This will send the email template to all matching users. This action cannot be undone.',
                  okText: 'Send',
                  okType: 'danger',
                  onOk: handleSend,
                });
              }}
            >
              Send to {matchedCount} Users
            </Button>
          </Space>
        </StepCard>
      )}

      {/* Step 4: Results */}
      {currentStep === 4 && sendResult && (
        <StepCard title="Send Results">
          <Alert
            message={sendResult.messages?.[0] || 'Complete'}
            type="success"
            style={{ marginBottom: 16 }}
          />
          {sendResult.results && (
            <Table
              dataSource={sendResult.results.slice(0, 50)}
              columns={[
                { title: 'Email', dataIndex: 'email', key: 'email' },
                { title: 'Status', dataIndex: 'status', key: 'status',
                  render: (s: string) => (
                    <Tag color={s === 'sent' ? 'green' : 'red'}>{s}</Tag>
                  ),
                },
                { title: 'Error', dataIndex: 'error', key: 'error',
                  render: (e: string) => e || '-' },
              ]}
              rowKey="email"
              size="small"
              pagination={false}
            />
          )}
          <Button style={{ marginTop: 16 }} onClick={() => {
            setCurrentStep(0);
            setSendResult(null);
            setSelectedTemplate(undefined);
          }}>
            Send Another
          </Button>
        </StepCard>
      )}
    </div>
  );
};

export default Email;
